import { useEffect, useRef, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { authService } from '../services/auth';

export default function VerifyEmailPage() {
  const { token } = useParams<{ token: string }>();
  const [status, setStatus] = useState<'loading' | 'success' | 'error'>('loading');
  const [message, setMessage] = useState('');
  const [verifiedEmail, setVerifiedEmail] = useState<string | null>(null);
  const hasCalledRef = useRef(false);

  useEffect(() => {
    if (!token) {
      setStatus('error');
      setMessage('No verification token provided.');
      return;
    }

    // Prevent double-call from React 18 StrictMode — the token is
    // consumed on the first request, so a second call would always fail.
    if (hasCalledRef.current) return;
    hasCalledRef.current = true;

    authService
      .verifyEmail(token)
      .then((data) => {
        setStatus('success');
        setMessage('Your email has been verified successfully.');
        if (data.email) setVerifiedEmail(data.email);
      })
      .catch((err) => {
        setStatus('error');
        setMessage(err.response?.data?.detail || 'Verification failed. The link may have expired.');
      });
  }, [token]);

  const loginUrl = verifiedEmail
    ? `/login?verified=1&email=${encodeURIComponent(verifiedEmail)}`
    : '/login?verified=1';

  return (
    <>
      <p className="mt-2 text-center text-sm text-slate-500">Email Verification</p>

      <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
        <div className="bg-white py-8 px-6 shadow-sm border border-slate-100 rounded-2xl sm:px-10 text-center">
          {status === 'loading' && <p className="text-slate-500">Verifying your email...</p>}

          {status === 'success' && (
            <>
              <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-xl bg-emerald-50 mb-4">
                <svg className="h-6 w-6 text-emerald-600" fill="none" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor" aria-hidden="true">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <h1 className="text-lg font-semibold text-slate-900">Email verified</h1>
              <p className="mt-2 text-sm text-slate-500">{message}</p>
              {verifiedEmail && (
                <p className="mt-1 text-xs text-slate-400">{verifiedEmail}</p>
              )}
              <Link
                to={loginUrl}
                className="mt-6 inline-block w-full py-2.5 px-4 rounded-xl text-sm font-semibold text-white bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 shadow-sm shadow-blue-500/25 transition-all"
              >
                Continue to sign in
              </Link>
            </>
          )}

          {status === 'error' && (
            <>
              <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-xl bg-red-50 mb-4">
                <svg className="h-6 w-6 text-red-600" fill="none" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor" aria-hidden="true">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </div>
              <h1 className="text-lg font-semibold text-slate-900">Verification failed</h1>
              <p className="mt-2 text-sm text-slate-500">{message}</p>
              <Link
                to="/check-email"
                className="mt-6 inline-block w-full py-2.5 px-4 rounded-xl text-sm font-semibold text-white bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 shadow-sm shadow-blue-500/25 transition-all"
              >
                Request a new link
              </Link>
              <Link
                to="/login"
                className="mt-3 inline-block text-sm text-slate-500 hover:text-slate-700"
              >
                Back to sign in
              </Link>
            </>
          )}
        </div>
      </div>
    </>
  );
}
